import React from "react";
import { Link } from "react-router";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Flag,
  Lightbulb,
  Scale,
  Shield,
  Sparkles,
} from "lucide-react";
import {
  ArticleCard,
  FAQItem,
  LegalTopicIntroHero,
  MediaCarousel,
  PodcastCard,
  SectionHead,
  VideoCard,
  foregroundOnSolidAccent,
} from "../shared/categoryDetailShared";

const ListBlock = ({ items, color, icon: Icon = CheckCircle2 }) => (
  <ul className="space-y-2.5">
    {items.map((line) => (
      <li key={line} className="flex gap-3 text-sm font-semibold leading-relaxed text-gray-800 sm:text-[0.9375rem]">
        <span
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${color}14`, color }}
        >
          <Icon size={14} strokeWidth={2.25} />
        </span>
        <span className="min-w-0 pt-0.5">{line}</span>
      </li>
    ))}
  </ul>
);

/** Overview — Criminal law in Ghana (rights, arrest, bail, defence). */
export const CriminalLawOverviewTab = ({ data, color }) => (
  <div className="space-y-10">
    <LegalTopicIntroHero
      color={color}
      categoryEyebrow="LEGAL · CRIMINAL LAW (GHANA)"
      funnelTagline={data.funnelTagline}
      FunnelIcon={Scale}
      gallerySrc={data.galleryImages[0]}
      galleryAlt="Legal documents and justice context — criminal law in Ghana"
      galleryImgClassName="object-[center_42%] sm:object-[center_38%]"
      imageBandIcon={Shield}
      imageBandLabel="Rights &amp; due process"
      imageSubtitle={data.focusAreas.join(" · ")}
      statsPreview={data.stats}
      photoEditorialFrame
    >
      <h2 className="max-w-2xl text-[1.55rem] font-black leading-[1.14] tracking-[-0.02em] text-slate-950 sm:text-[1.9rem] lg:text-[2.05rem]">
        {data.introduction.headline}
      </h2>
      <p className="mt-5 max-w-prose text-base leading-relaxed text-slate-600 sm:text-[1.05rem] sm:leading-relaxed">{data.introduction.lead}</p>

      <div className="mt-8 rounded-2xl border border-slate-200/95 bg-white/90 p-5 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.85)] backdrop-blur-sm sm:p-7">
        <ListBlock items={data.introduction.bullets} color={color} />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {data.focusAreas.map((tag) => (
          <span
            key={tag}
            className="rounded-xl border border-slate-200/95 bg-white/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 shadow-sm ring-1 ring-slate-200/55"
          >
            {tag}
          </span>
        ))}
      </div>

      <p className="mt-8 text-xs font-black uppercase tracking-[0.26em] text-slate-500">{data.introduction.pointerLead}</p>

      <div className="mt-4 rounded-2xl border border-cyan-950/10 bg-gradient-to-br from-slate-50 to-cyan-50/35 px-5 py-5 sm:px-7 sm:py-6">
        <p className="text-sm font-black text-slate-950">{data.introduction.leadsToHeading}</p>
        <ul className="mt-4 space-y-2">
          {data.introduction.leadsTo.map((x) => (
            <li key={x} className="flex items-start gap-2 text-sm font-semibold text-slate-800">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden />
              <span>{x}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 flex items-start gap-2 border-t border-slate-200/80 pt-5 text-sm font-bold leading-relaxed text-slate-900">
          <Lightbulb size={17} className="mt-0.5 shrink-0" style={{ color }} aria-hidden />
          {data.introduction.closing}
        </p>
      </div>
    </LegalTopicIntroHero>

    <section>
      <SectionHead eyebrow="Framework" title={data.whatIs.title} subtitle={data.whatIs.dealsWithHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <ul className="space-y-2 text-sm font-semibold text-slate-800">
            {data.whatIs.dealsWith.map((x) => (
              <li key={x}>• {x}</li>
            ))}
          </ul>
          <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.whatIs.governedHeading}</p>
          <ul className="mt-3 space-y-2">
            {data.whatIs.governed.map((g) => (
              <li key={g} className="text-sm font-bold text-slate-900">
                • {g}
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-xl border p-5" style={{ borderColor: `${color}44`, background: `${color}08` }}>
            <p className="text-sm font-black text-slate-900">{data.whatIs.dualLead}</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {data.whatIs.dual.map((d) => (
                <li key={d}>• {d}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.arrest.title}
          </p>
          <p className="mt-4 text-sm font-black text-slate-950">{data.arrest.warrantHeading}</p>
          <p className="mt-1 text-sm font-semibold text-slate-600">{data.arrest.warrantLead}</p>
          <p className="mt-3 text-sm text-slate-700">{data.arrest.warrantSub}</p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
            {data.arrest.whoCanArrest.map((w) => (
              <li key={w}>• {w}</li>
            ))}
          </ul>
          <div className="mt-6 rounded-xl border border-emerald-200/80 bg-emerald-50/50 p-5">
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-emerald-900">
              <CheckCircle2 size={14} className="text-emerald-600" aria-hidden />
              {data.arrest.rightsHeading}
            </p>
            <ul className="mt-3 space-y-1.5 text-sm font-semibold text-emerald-950/90">
              {data.arrest.rights.map((r) => (
                <li key={r}>• {r}</li>
              ))}
            </ul>
          </div>
          <div className="mt-4 rounded-xl border border-amber-200/80 bg-amber-50/55 p-5">
            <p className="flex items-center gap-2 text-sm font-black text-amber-950">
              <AlertTriangle size={16} className="shrink-0 text-amber-600" aria-hidden />
              {data.arrest.ruleHeading}
            </p>
            <p className="mt-2 text-sm font-semibold text-amber-950/95">{data.arrest.ruleBody}</p>
          </div>
          <p className="mt-6 text-xs font-black uppercase tracking-wider text-slate-500">{data.arrest.whatToDoHeading}</p>
          <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
            {data.arrest.whatToDo.map((w) => (
              <li key={w}>• {w}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Custody" title={data.bail.title} subtitle={data.bail.whatHeading} color={color} />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200/90 bg-white p-6">
            <p className="text-sm font-semibold text-slate-700">{data.bail.whatBody}</p>
            <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.bail.howHeading}</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-800">
              {data.bail.howMayNeed.map((h) => (
                <li key={h}>• {h}</li>
              ))}
            </ul>
            <p className="mt-4 text-sm font-bold text-slate-900">{data.bail.howFootnote}</p>
          </div>
          <div className="rounded-xl border border-slate-900/[0.08] bg-slate-50 p-6">
            <p className="text-sm font-black text-slate-900">{data.bail.ruleHeading}</p>
            <p className="mt-2 text-sm font-semibold text-slate-700">{data.bail.ruleBody}</p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
          <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
            {data.bail.typesHeading}
          </p>
          <ol className="mt-6 space-y-6">
            {data.bail.types.map((t) => (
              <li key={t.number} className="flex gap-4">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-black shadow-md"
                  style={{ background: color, color: foregroundOnSolidAccent(color) }}
                >
                  {t.number}
                </span>
                <div>
                  <p className="font-black text-slate-950">{t.title}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-600">{t.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 rounded-xl border border-red-200/70 bg-red-50/40 p-5">
            <p className="text-xs font-black uppercase tracking-wider text-red-900">{data.bail.difficultHeading}</p>
            <ul className="mt-2 space-y-1 text-sm font-semibold text-red-950/90">
              {data.bail.difficult.map((d) => (
                <li key={d}>• {d}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="grid gap-6 xl:grid-cols-12">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8 xl:col-span-6">
        <SectionHead eyebrow="Representation" title={data.legalDefense.title} subtitle={data.legalDefense.whyHeading} color={color} />
        <ul className="mt-2 space-y-1.5 text-sm font-semibold text-slate-800">
          {data.legalDefense.whyBullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-8 text-xs font-black uppercase tracking-wider text-slate-500">{data.legalDefense.rightsHeading}</p>
        <ul className="mt-2 space-y-1 text-sm font-semibold text-slate-700">
          {data.legalDefense.legalRights.map((r) => (
            <li key={r}>• {r}</li>
          ))}
        </ul>
      </div>
      <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-6 sm:p-8 xl:col-span-6">
        <p className="flex items-center gap-2 text-sm font-black text-amber-950">
          <AlertTriangle size={18} className="shrink-0 text-amber-600" aria-hidden />
          {data.legalDefense.mistakeHeading}
        </p>
        <p className="mt-3 text-sm font-semibold leading-relaxed text-amber-950/95">{data.legalDefense.mistakeBody}</p>
      </div>
    </section>

    <section className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <SectionHead eyebrow="Procedure" title={data.courtProcess.title} subtitle={data.courtProcess.afterArrestHeading} color={color} />
        <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm font-semibold text-slate-800">
          {data.courtProcess.afterArrestSteps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <p className="mt-8 text-xs font-black uppercase tracking-wider" style={{ color }}>
          {data.courtProcess.stagesHeading}
        </p>
        <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm font-semibold text-slate-700">
          {data.courtProcess.stages.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </div>
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.courtProcess.courtsHeading}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
          {data.courtProcess.courts.map((c) => (
            <li key={c}>• {c}</li>
          ))}
        </ul>
        <div className="mt-10 rounded-xl border border-slate-900/[0.08] bg-slate-900/[0.03] p-6">
          <p className="text-xs font-black uppercase tracking-wider text-slate-500">{data.trialRights.title}</p>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-900">
            {data.trialRights.rights.map((r) => (
              <li key={r}>• {r}</li>
            ))}
          </ul>
          <div className="mt-8 border-t border-slate-900/10 pt-6">
            <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color }}>
              {data.trialRights.principleHeading}
            </p>
            <p className="mt-3 text-lg font-black text-slate-950">{data.trialRights.principleQuote}</p>
          </div>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="On the ground" title={data.commonIssues.title} subtitle={data.commonIssues.intro} color={color} />
      <div className="rounded-xl border border-slate-200/90 bg-white p-6 sm:p-8">
        <ul className="grid gap-2 sm:grid-cols-2">
          {data.commonIssues.items.map((i) => (
            <li
              key={i}
              className="rounded-lg border border-slate-100 bg-slate-50/70 px-4 py-3 text-sm font-semibold text-slate-900"
            >
              • {i}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Everyday playbook" title={data.practicalTips.title} color={color} />
      <div className="grid gap-4 md:grid-cols-3">
        {[data.practicalTips.ifArrested, data.practicalTips.ifHelping, data.practicalTips.duringProcess].map((block) => (
          <div key={block.heading} className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wider" style={{ color }}>
              {block.heading}
            </p>
            <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-700">
              {block.items.map((it) => (
                <li key={it}>• {it}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Protect yourself" title={data.redFlags.title} color={color} />
      <div className="rounded-xl border border-red-200/70 bg-red-50/35 p-6 sm:p-8">
        <ul className="space-y-3">
          {data.redFlags.items.map((r) => (
            <li key={r} className="flex gap-3 text-sm font-semibold text-red-950">
              <Flag size={16} className="mt-0.5 shrink-0 text-red-500" aria-hidden />
              {r}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Editorial" title={data.editorialPlaybook.title} color={color} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.articlesHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.articles.map((a) => (
              <li key={a}>• {a}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.videosHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.videos.map((v) => (
              <li key={v}>• {v}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200/90 bg-white p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{data.editorialPlaybook.leadCtasHeading}</p>
          <ul className="mt-3 space-y-2 text-sm font-semibold text-slate-800">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/60 p-8 text-white sm:p-10">
      <div className="pointer-events-none absolute inset-0 opacity-20 blur-3xl" style={{ background: color }} aria-hidden />
      <div className="relative z-10">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.scenario.eyebrow}
        </p>
        <h3 className="mt-3 text-xl font-black sm:text-2xl">{data.scenario.title}</h3>
        <p className="mt-4 text-sm font-bold text-white/90">{data.scenario.exampleLead}</p>
        <ul className="mt-3 space-y-2 text-sm font-semibold text-white/85">
          {data.scenario.bullets.map((b) => (
            <li key={b}>• {b}</li>
          ))}
        </ul>
        <p className="mt-6 text-xs font-black uppercase tracking-wider text-white/50">{data.scenario.outcomesHeading}</p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {data.scenario.outcomes.map((o) => (
            <li
              key={o}
              className="rounded-full border border-white/20 bg-white/[0.08] px-3 py-1 text-xs font-bold text-white"
            >
              {o}
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Funnel" title={data.funnelApplication.title} color={color} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[data.funnelApplication.hook, data.funnelApplication.education, data.funnelApplication.authority, data.funnelApplication.conversion].map(
          (step) => (
            <div key={step.label} className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color }}>
                {step.label}
              </p>
              <p className="mt-3 text-sm font-bold leading-relaxed text-slate-900">{step.text}</p>
            </div>
          ),
        )}
      </div>
    </section>

    <section className="relative overflow-hidden rounded-xl border border-slate-900/10 bg-slate-950 p-8 text-white sm:p-10">
      <div
        className="absolute inset-0 opacity-30 blur-3xl"
        style={{ background: `radial-gradient(circle at 30% 20%, ${color}, transparent 55%)` }}
        aria-hidden
      />
      <div className="relative">
        <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
          {data.finalTakeaway.title}
        </p>
        <ul className="mt-6 space-y-4">
          {data.finalTakeaway.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-base font-bold leading-relaxed text-white/95 sm:text-lg">
              <CheckCircle2 size={22} className="shrink-0 opacity-90" style={{ color }} aria-hidden />
              {b}
            </li>
          ))}
        </ul>
        <div className="mt-10 border-t border-white/10 pt-8">
          <p className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.bottomLine.title}
          </p>
          <p className="mt-3 text-lg font-black text-white sm:text-xl">{data.bottomLine.lead}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center">
            <p className="rounded-lg border border-white/15 bg-white/[0.06] p-4 text-center text-sm font-bold text-white sm:text-base">{data.bottomLine.smooth}</p>
            <span className="hidden text-center text-sm font-black text-white/50 sm:block">{data.bottomLine.and}</span>
            <p className="rounded-lg border border-red-500/30 bg-red-950/35 p-4 text-center text-sm font-bold text-red-100 sm:text-base">{data.bottomLine.stressful}</p>
          </div>
          <p className="mt-6 flex flex-wrap items-center gap-2 text-sm font-semibold text-white/70">
            <ArrowRight size={16} style={{ color }} aria-hidden />
            {data.bottomLine.closing}
          </p>
        </div>
      </div>
    </section>

    <section>
      <SectionHead eyebrow="Frequently Asked" title="Common Questions" color={color} />
      <div className="space-y-2">
        {data.faqs.map((faq) => (
          <FAQItem key={faq.q} q={faq.q} a={faq.a} color={color} />
        ))}
      </div>
    </section>
  </div>
);

export const CriminalLawArticlesTab = ({ data, color, industryId, slug, ctaTo }) => (
  <div className="space-y-10">
    <section>
      <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <SectionHead eyebrow="Content Library" title="Articles &amp; Guides" subtitle={data.funnelTagline} color={color} />
        <span className="mb-8 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {data.articles.length} articles
        </span>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.articles.map((article) => (
          <ArticleCard key={article.id} article={article} to={`/${industryId}/${slug}/${article.slug}`} color={color} />
        ))}
      </div>
    </section>

    <section
      className="overflow-hidden rounded-xl p-8 sm:p-10"
      style={{
        background: `linear-gradient(128deg, ${color}12, transparent)`,
        border: `1px solid ${color}35`,
      }}
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="mb-2 text-[10px] font-black uppercase tracking-[0.28em]" style={{ color }}>
            {data.editorialPlaybook.leadCtasHeading}
          </p>
          <h3 className="text-xl font-black text-slate-950 sm:text-2xl">{data.primaryCtaHeading}</h3>
          <ul className="mt-4 space-y-2 text-sm font-semibold text-slate-600">
            {data.editorialPlaybook.leadCtas.map((c) => (
              <li key={c}>• {c}</li>
            ))}
          </ul>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl px-7 py-4 text-sm font-black shadow-md transition hover:scale-[1.02]"
          style={{ background: color, color: foregroundOnSolidAccent(color) }}
        >
          Register Free
          <ArrowRight size={15} aria-hidden />
        </Link>
      </div>
    </section>
  </div>
);

export const CriminalLawMediaTab = ({ data, color }) => {
  const videos = data.videos ?? [];
  const podcasts = data.podcasts ?? [];
  const total = videos.length + podcasts.length;

  return (
    <div className="min-w-0 space-y-10 sm:space-y-12">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHead
          eyebrow="Watch &amp; listen"
          title="Videos &amp; podcasts"
          subtitle={data.editorialPlaybook.videos.join(" · ")}
          color={color}
        />
        <span className="mt-1 shrink-0 rounded-lg px-3 py-1 text-xs font-black" style={{ background: `${color}18`, color }}>
          {total} featured
        </span>
      </div>

      {videos.length > 0 ? (
        <section aria-labelledby="cl-media-videos-heading" className="min-w-0">
          <MediaCarousel
            variant="video"
            color={color}
            ariaLabel="Criminal law videos"
            items={videos}
            header={<h3 id="cl-media-videos-heading" className="text-lg font-black text-slate-950 sm:text-xl">Videos</h3>}
            meta={`${videos.length} in library`}
            renderItem={(video) => <VideoCard video={video} color={color} />}
          />
        </section>
      ) : null}

      {podcasts.length > 0 ? (
        <section aria-labelledby="cl-media-podcasts-heading" className="min-w-0">
          <MediaCarousel
            variant="podcast"
            color={color}
            ariaLabel="Criminal law podcast episodes"
            items={podcasts}
            header={<h3 id="cl-media-podcasts-heading" className="text-lg font-black text-slate-950 sm:text-xl">Podcasts</h3>}
            meta={`${podcasts.length} episodes`}
            renderItem={(episode) => <PodcastCard podcast={episode} color={color} />}
          />
          <p className="mt-4 px-2 text-center text-xs leading-relaxed text-gray-400 sm:text-left sm:text-sm md:mt-6">
            Listen links appear here when Spotify, Apple Podcasts, or other feeds are connected from admin or your CMS.
          </p>
        </section>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-slate-950 text-white shadow-lg sm:rounded-[1.5rem]">
        <div className="grid gap-0 lg:grid-cols-2">
          <div className="p-6 sm:p-8 md:p-10">
            <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles size={11} style={{ color }} aria-hidden />
              For partners
            </div>
            <h3 className="text-xl font-black leading-snug sm:text-2xl">{data.funnelApplication.conversion.text}</h3>
            <p className="mt-3 text-sm font-bold text-white/85">{data.primaryCtaHeading}</p>
            <Link
              to="/partners/onboard"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm font-black"
              style={{ background: color, color: foregroundOnSolidAccent(color) }}
            >
              Become a partner
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
          <div className="relative min-h-[12rem] sm:min-h-[14rem] lg:min-h-0">
            <img src={data.galleryImages[1]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-slate-950/85" />
          </div>
        </div>
      </div>
    </div>
  );
};
